import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { galleriesPublicApi } from '../../api/adminApi';
import { galleryId, parseGalleryImageEntries } from '../Admin/Galleries/galleryHelpers';
import '../AboutPage/AboutPage.css';
import './GalleryPage.css';

function GalleryCategoryPage() {
  const { id } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    galleriesPublicApi
      .getOne(id)
      .then((data) => {
        if (!cancelled) setGallery(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) setNotFound(true);
        setGallery(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const goPrev = useCallback(() => {
    setLightboxIndex((i) => {
      if (i == null) return i;
      const entries = parseGalleryImageEntries(gallery);
      const n = entries.length;
      if (n <= 0) return null;
      return (i - 1 + n) % n;
    });
  }, [gallery]);

  const goNext = useCallback(() => {
    setLightboxIndex((i) => {
      if (i == null) return i;
      const entries = parseGalleryImageEntries(gallery);
      const n = entries.length;
      if (n <= 0) return null;
      return (i + 1) % n;
    });
  }, [gallery]);

  useEffect(() => {
    if (lightboxIndex == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIndex, closeLightbox, goPrev, goNext]);

  if (!id) {
    return <Navigate to="/gallery" replace />;
  }

  if (loading) {
    return (
      <main className="gallery-page gallery-page--detail">
        <section className="about-banner" aria-label="Life At our preschool">
          <div className="hero-inner">
            <div className="hero-grid">
              <div className="hero-content">
                <h1 className="about-banner-title">Life At Our Preschool</h1>
                <p className="about-banner-desc">
                  We maintain the highest standards of safety and care to give you peace of mind.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="gallery-detail-section">
          <div className="gallery-section-inner">
            <p className="gallery-page-loading">Loading gallery…</p>
          </div>
        </section>
      </main>
    );
  }

  if (notFound || !gallery || !galleryId(gallery)) {
    return <Navigate to="/gallery" replace />;
  }

  const imageEntries = parseGalleryImageEntries(gallery);
  const lightboxOpen = lightboxIndex != null && imageEntries.length > 0;
  const lightboxImg = lightboxOpen ? imageEntries[lightboxIndex] : null;

  return (
    <main className="gallery-page gallery-page--detail">
      <section className="about-banner" aria-label="Life At our preschool">
        <div className="hero-inner">
          <div className="hero-grid">
            <div className="hero-content">
              <h1 className="about-banner-title">Life At Our Preschool</h1>
              <p className="about-banner-desc">
                We maintain the highest standards of safety and care to give you peace of mind.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="gallery-detail-section" aria-labelledby="gallery-detail-heading">
        <div className="gallery-section-inner">
          <p className="gallery-detail-back">
            <Link to="/gallery" className="gallery-back-link">
              ← Back to Gallery
            </Link>
          </p>
          <h2 id="gallery-detail-heading" className="gallery-detail-title">
            {gallery.title || 'Image Gallery'}
          </h2>
          {imageEntries.length === 0 ? (
            <p className="gallery-page-empty">No photos in this gallery yet.</p>
          ) : (
            <div className="gallery-detail-grid">
              {imageEntries.map((img, idx) => (
                <button
                  key={img.key || `${galleryId(gallery)}-${idx}`}
                  type="button"
                  className="gallery-detail-cell"
                  onClick={() => setLightboxIndex(idx)}
                  aria-label={
                    gallery.title
                      ? `Open ${gallery.title} photo ${idx + 1} full size`
                      : `Open gallery photo ${idx + 1} full size`
                  }
                >
                  <img
                    src={img.url}
                    alt={gallery.title ? `${gallery.title} — photo ${idx + 1}` : `Gallery photo ${idx + 1}`}
                    className="gallery-detail-img"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {lightboxOpen && lightboxImg && (
        <div
          className="gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Full size photo"
        >
          <button
            type="button"
            className="gallery-lightbox-backdrop"
            aria-label="Close full size photo"
            onClick={closeLightbox}
          />
          <div className="gallery-lightbox-inner">
            <button
              type="button"
              className="gallery-lightbox-close"
              onClick={closeLightbox}
              aria-label="Close"
            >
              ×
            </button>
            {imageEntries.length > 1 && (
              <>
                <button
                  type="button"
                  className="gallery-lightbox-nav gallery-lightbox-nav--prev"
                  onClick={goPrev}
                  aria-label="Previous photo"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="gallery-lightbox-nav gallery-lightbox-nav--next"
                  onClick={goNext}
                  aria-label="Next photo"
                >
                  ›
                </button>
              </>
            )}
            <img
              src={lightboxImg.url}
              alt={
                gallery.title
                  ? `${gallery.title} — photo ${lightboxIndex + 1} full size`
                  : `Gallery photo ${lightboxIndex + 1} full size`
              }
              className="gallery-lightbox-img"
            />
          </div>
        </div>
      )}
    </main>
  );
}

export default GalleryCategoryPage;
